/* eslint-disable max-lines */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pinningEnabledArtworksAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  type DesignLabPlacementKey,
  useCreatePinningSession
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinsCtaClicked,
  trackDesignLabPinningModeEntered
} from "@design-lab/shared/lib/analytics";
import {
  useDesignLabPinSessionRecording,
  usePinBasedRedrawEnabled
} from "@design-lab/shared/lib/config";
import { startPinSessionRecording } from "@design-lab/shared/lib/pinSessionRecording";
import { useToast } from "@design-lab/shared/ui";
import { StarsIcon } from "@design-lab/shared/ui/icons";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useWatch } from "react-hook-form";
import type { ArtworkForm } from "@design-lab/shared/types";
import type { models } from "jiffy-api";
import { CompletedSessionIndicator } from "./CompletedSessionIndicator";

type RequestDesignChangesProps = {
  placementSide?: "front" | "back";
  currentValue?: models.cart.madeAdd.EnhancementTier | undefined;
  removeBackground?: boolean;
  canRemoveBackground?: boolean;
  editImageManual?: boolean;
};

export function RequestDesignChanges({
  placementSide = "front",
  currentValue,
  removeBackground = false,
  canRemoveBackground = true,
  editImageManual = false
}: RequestDesignChangesProps) {
  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const { mutate: createPinningSession, isPending: isCreatingPinningSession } =
    useCreatePinningSession();
  const { error: showErrorToast } = useToast();
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();
  const pinSessionRecordingEnabled = useDesignLabPinSessionRecording();
  const pinningEnabledArtworks = useAtomValue(pinningEnabledArtworksAtom);

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const selectedFrontPlacement = artworkForm?.selectedFrontPlacement as
    | DesignLabPlacementKey
    | undefined;
  let placementKey: DesignLabPlacementKey = "front";
  if (placementSide === "back") {
    placementKey = "back";
  } else if (selectedFrontPlacement === "frontLeftChest") {
    placementKey = "frontLeftChest";
  }

  const artworkIdForm = (
    artworkForm?.[placementKey]?.artworkId ? String(artworkForm[placementKey]?.artworkId) : ""
  ) as string;

  // Check if pinning is enabled for this artwork (based on ownership)
  // If artworkId is not in the map, assume enabled (backwards compatibility)
  const isPinningEnabled = artworkIdForm ? (pinningEnabledArtworks[artworkIdForm] ?? true) : true;

  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions
  });
  const hasActiveSession = pinBasedRedrawEnabled && sessionForPlacement?.state === "active";
  const hasCompletedSession = sessionForPlacement?.state === "completed";

  // Determine the preview image name based on current selection
  // This matches the backend logic in dtf_print.rb artwork_preview_image_name
  const getPreviewImageName = useCallback((): string | undefined => {
    if (!currentValue) return undefined;

    if (currentValue === "print_original") {
      return "original_image";
    }

    if (currentValue === "full_enhancements") {
      if (editImageManual) {
        return "manual_edit_image";
      }
      if (removeBackground && canRemoveBackground) {
        return "image_no_background";
      }
      return "image_with_background";
    }

    return undefined;
  }, [currentValue, removeBackground, canRemoveBackground, editImageManual]);

  const handleRequestDesignerEdit = useCallback(() => {
    if (isCreatingPinningSession) return;
    const artworkId = artworkIdForm;
    if (!artworkId) return;
    if (!designProjectPlacementId) {
      showErrorToast({
        title: "Unable to start design instructions",
        description: "Please wait a moment and try again."
      });
      return;
    }

    const previewImageName = getPreviewImageName();

    trackDesignLabPinsCtaClicked(artworkId);

    // Reuse existing active session if one exists for this artwork
    const existingSession = sessionForPlacement;
    if (existingSession?.state === "active") {
      trackDesignLabPinningModeEntered({
        artworkId,
        sessionId: existingSession.sessionId,
        hasExistingPins: true
      });
      startPinSessionRecording({ enabled: pinSessionRecordingEnabled });
      setCurrentPinningSession({
        designProjectPlacementId,
        artworkId,
        sessionId: existingSession.sessionId
      });
      setPinningMode(true);
      return;
    }

    const sessionParams = {
      designProjectPlacementId,
      artworkId,
      ...(previewImageName && { previewImageName })
    };

    createPinningSession(sessionParams, {
      onSuccess: (data: { sessionId: string }) => {
        trackDesignLabPinningModeEntered({
          artworkId,
          sessionId: data.sessionId,
          hasExistingPins: false
        });
        startPinSessionRecording({ enabled: pinSessionRecordingEnabled });
        setPinningSessions((prev: typeof pinningSessions) => ({
          ...prev,
          [designProjectPlacementId]: {
            sessionId: data.sessionId,
            state: "active",
            summary: null
          }
        }));
        setCurrentPinningSession({
          designProjectPlacementId,
          artworkId,
          sessionId: data.sessionId
        });
        setPinningMode(true);
      },
      onError: () => {
        showErrorToast({
          title: "Unable to start design instructions",
          description: "Something went wrong. Please try again."
        });
      }
    });
  }, [
    isCreatingPinningSession,
    artworkIdForm,
    designProjectPlacementId,
    getPreviewImageName,
    sessionForPlacement,
    createPinningSession,
    setPinningSessions,
    setCurrentPinningSession,
    setPinningMode,
    pinSessionRecordingEnabled,
    showErrorToast
  ]);

  // Completed pinning session indicator
  if (hasCompletedSession) {
    return <CompletedSessionIndicator placementKey={placementKey} placementSide={placementSide} />;
  }

  if (!pinBasedRedrawEnabled || hasActiveSession || !isPinningEnabled) {
    return null;
  }

  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-neutral-950">
          How can we make your image better?
        </span>
      </div>
      <button
        type="button"
        data-testid="request-designer-edit-button-print-as-is"
        className="border-cta-gradient flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg !border-[1px] p-3 text-sm font-medium text-neutral-950 hover:![background-image:linear-gradient(#f3f4f6,#f3f4f6),linear-gradient(103.21deg,#a855f7_6.2%,#2563eb_108.53%)]"
        onClick={handleRequestDesignerEdit}
        disabled={isCreatingPinningSession}
      >
        <StarsIcon className="h-5 w-5" />
        <span>Get Free Artist Edits</span>
      </button>
    </div>
  );
}
